import { HStack, Box, Text } from '@chakra-ui/react'
import { useMemo } from 'react'

export type UiOrderStatus =
  | 'ABGESENDET'
  | 'WIRD_UEBERPRUEFT'
  | 'IN_BEARBEITUNG'
  | 'KORREKTUR'
  | 'KORREKTUR_AKZEPTIERT'
  | 'FREIGEGEBEN'
  | 'ABGELEHNT'
  | 'KORREKTUR_ABGELEHNT'
  | string

interface Props {
  status: UiOrderStatus
  /** Overrides the label derived from `status` (dot color still follows `status`). */
  label?: string
}

/**
 * Reusable Status Pill
 * Matches SIB’s design language:
 * - Dot + label
 * - Chakra token colors
 */
export function OrderStatusPill({ status, label: labelOverride }: Props) {
  const { label: derivedLabel, dotColor } = useMemo(() => {
    switch (status) {
      case 'ABGESENDET':
        return { label: 'Abgesendet', dotColor: 'yellow.400' }

      case 'WIRD_UEBERPRUEFT':
        return { label: 'Wird überprüft', dotColor: 'yellow.500' }

      case 'IN_BEARBEITUNG':
        return { label: 'In Bearbeitung', dotColor: 'orange.400' }

      case 'KORREKTUR':
        return { label: 'Korrektur', dotColor: 'blue.500' }

      case 'KORREKTUR_AKZEPTIERT':
        return { label: 'Korrektur akzeptiert', dotColor: 'green.500' }

      case 'FREIGEGEBEN':
        return { label: 'Freigegeben', dotColor: 'green.400' }

      case 'ABGELEHNT':
        return { label: 'Abgelehnt', dotColor: 'red.500' }

      case 'KORREKTUR_ABGELEHNT':
        return { label: 'Korrektur abgelehnt', dotColor: 'red.400' }

      default:
        return { label: status, dotColor: 'gray.400' }
    }
  }, [status])

  const label = labelOverride ?? derivedLabel

  return (
    <HStack gap={2} align="center">
      <Box
        boxSize="2.5"
        rounded="full"
        bg={dotColor} // OK: dot uses semantic accent colors
        flexShrink={0}
      />
      <Text fontWeight="normal" fontSize="sm" color="fg">
        {label}
      </Text>
    </HStack>
  )
}

export default OrderStatusPill
